/**
 * Smart Import — agent-flow state machine.
 *
 * The UI IS the state machine. Each phase renders exactly one active card;
 * prior cards live in `scrollback` as chat bubbles, creating a conversational
 * transcript above the current action.
 *
 * Tested in __tests__/state.test.ts — all transitions are pure.
 */

import type {
  ColumnConfirmResult,
  ConfirmResult,
  MmOption,
  UploadResult,
} from './api'

export type Phase =
  | { phase: 'idle' }
  | { phase: 'need-mm-connection' }
  | { phase: 'uploading' }
  | { phase: 'column-confirm'; upload: UploadResult }
  | { phase: 'column-preview'; jobId: string; confirm: ColumnConfirmResult; upload: UploadResult; columnIndex: number }
  | { phase: 'event-pick'; jobId: string; barcodeCount: number; events: MmOption[] }
  | { phase: 'plan-pick'; jobId: string; barcodeCount: number; eventId: number; eventName: string; plans: MmOption[] }
  | { phase: 'name-it'; jobId: string; barcodeCount: number; eventId: number; eventName: string; planId: number; planName: string; defaultName: string }
  | { phase: 'submitting'; jobId: string }
  | { phase: 'success'; result: ConfirmResult }
  | { phase: 'failed'; reason: string }

export interface ScrollbackBubble {
  id: string
  from: 'agent' | 'user'
  text: string
}

export interface State {
  current: Phase
  scrollback: ScrollbackBubble[]
  // Last seen MM-session check; drives which card we land on from idle.
  mmConnected: boolean
}

export type Action =
  | { type: 'mm-session-known'; connected: boolean }
  | { type: 'mm-connect-start' }
  | { type: 'mm-connect-ok' }
  | { type: 'mm-disconnect' }
  | { type: 'file-selected' }
  | { type: 'upload-ok'; upload: UploadResult }
  | { type: 'upload-failed'; reason: string }
  | { type: 'column-confirmed'; columnIndex: number; jobId: string; confirm: ColumnConfirmResult; headerName: string; upload: UploadResult }
  | { type: 'column-rejected'; reason: string }
  | { type: 'preview-accepted'; events: MmOption[] }
  | { type: 'event-picked'; eventId: number; eventName: string; plans: MmOption[] }
  | { type: 'plan-picked'; planId: number; planName: string; defaultName: string }
  | { type: 'name-updated'; name: string }
  | { type: 'submit-start' }
  | { type: 'submit-ok'; result: ConfirmResult }
  | { type: 'submit-failed'; reason: string }
  | { type: 'back-to-upload' }
  | { type: 'reset' }

export const initialState: State = {
  current: { phase: 'idle' },
  scrollback: [],
  mmConnected: false,
}

let bubbleCounter = 0
function bubble(
  from: ScrollbackBubble['from'],
  text: string,
): ScrollbackBubble {
  bubbleCounter += 1
  return { id: `b-${bubbleCounter}`, from, text }
}

// Exposed for tests so counter resets are deterministic.
export function resetBubbleCounter(): void {
  bubbleCounter = 0
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'mm-session-known': {
      if (state.current.phase !== 'idle') return state
      const next: Phase = action.connected
        ? { phase: 'idle' }
        : { phase: 'need-mm-connection' }
      return { ...state, mmConnected: action.connected, current: next }
    }

    case 'mm-connect-start':
      return state

    case 'mm-connect-ok':
      return {
        ...state,
        mmConnected: true,
        current: { phase: 'idle' },
        scrollback: [
          ...state.scrollback,
          bubble('agent', 'Connected to Magic Money. Drop a file whenever.'),
        ],
      }

    case 'mm-disconnect':
      return {
        ...state,
        mmConnected: false,
        current: { phase: 'need-mm-connection' },
      }

    case 'file-selected':
      return { ...state, current: { phase: 'uploading' } }

    case 'upload-ok': {
      const scrollback = [
        ...state.scrollback,
        bubble('user', 'Here it is.'),
      ]
      return {
        ...state,
        current: { phase: 'column-confirm', upload: action.upload },
        scrollback,
      }
    }

    case 'upload-failed':
      return {
        ...state,
        current: { phase: 'failed', reason: action.reason },
      }

    case 'column-confirmed': {
      const scrollback = [
        ...state.scrollback,
        bubble('user', `Use column "${action.headerName}".`),
      ]
      return {
        ...state,
        current: {
          phase: 'column-preview',
          jobId: action.jobId,
          confirm: action.confirm,
          upload: action.upload,
          columnIndex: action.columnIndex,
        },
        scrollback,
      }
    }

    case 'column-rejected':
      return {
        ...state,
        current: { phase: 'failed', reason: action.reason },
      }

    case 'preview-accepted': {
      if (state.current.phase !== 'column-preview') return state
      const scrollback = [
        ...state.scrollback,
        bubble('user', 'Looks right.'),
      ]
      return {
        ...state,
        current: {
          phase: 'event-pick',
          jobId: state.current.jobId,
          barcodeCount: state.current.confirm.barcodeCount,
          events: action.events,
        },
        scrollback,
      }
    }

    case 'event-picked': {
      if (state.current.phase !== 'event-pick') return state
      const scrollback = [
        ...state.scrollback,
        bubble('user', `Event: ${action.eventName}.`),
      ]
      return {
        ...state,
        current: {
          phase: 'plan-pick',
          jobId: state.current.jobId,
          barcodeCount: state.current.barcodeCount,
          eventId: action.eventId,
          eventName: action.eventName,
          plans: action.plans,
        },
        scrollback,
      }
    }

    case 'plan-picked': {
      if (state.current.phase !== 'plan-pick') return state
      const scrollback = [
        ...state.scrollback,
        bubble('user', `Plan: ${action.planName}.`),
      ]
      return {
        ...state,
        current: {
          phase: 'name-it',
          jobId: state.current.jobId,
          barcodeCount: state.current.barcodeCount,
          eventId: state.current.eventId,
          eventName: state.current.eventName,
          planId: action.planId,
          planName: action.planName,
          defaultName: action.defaultName,
        },
        scrollback,
      }
    }

    case 'name-updated': {
      if (state.current.phase !== 'name-it') return state
      return {
        ...state,
        current: { ...state.current, defaultName: action.name },
      }
    }

    case 'submit-start': {
      if (state.current.phase !== 'name-it') return state
      return {
        ...state,
        current: { phase: 'submitting', jobId: state.current.jobId },
      }
    }

    case 'submit-ok':
      return {
        ...state,
        current: { phase: 'success', result: action.result },
      }

    case 'submit-failed':
      return {
        ...state,
        current: { phase: 'failed', reason: action.reason },
      }

    case 'back-to-upload':
      return {
        ...state,
        current: { phase: 'idle' },
      }

    case 'reset':
      return { ...initialState, mmConnected: state.mmConnected }

    default:
      return state
  }
}

export function defaultBatchName(
  eventName: string,
  planName: string,
  now: Date = new Date(),
): string {
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const yyyy = now.getFullYear()
  return `${eventName} · ${planName} · ${mm}/${dd}/${yyyy}`
}
